import React, { useEffect, useRef, useState } from 'react';
import EventListItem from './EventListItem';
import AdCard from './AdCard';
import { nextAd } from '../lib/ads';
import { resolveEventLocation } from '../lib/location';

/**
 * Upcoming Events — homepage feed. Data comes from the feed-data edge function.
 * The first page (next events, oldest-first) is passed in as props from
 * main.jsx, then further pages are appended when the visitor clicks
 * "Load more events" via `loadMorePage`.
 *
 * Sponsored ads are interleaved as the 4th post, then every 7 posts after it.
 * Each ad is dealt from the shared pool (lib/ads) so feed and story ads never
 * repeat until every sponsor has appeared once.
 */
export default function UpcomingEvents({
    events = [], businesses = [], locations = [], analytics,
    loading = false, error = false, hasMore = false, loadMorePage = null,
}) {
    const [list, setList] = useState(null);
    const [hasMoreState, setHasMoreState] = useState(hasMore);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const initialized = useRef(false);
    const allocatedAds = useRef([]);

    const mergeById = (existing, incoming) => {
        const merged = new Map();
        for (const item of existing || []) merged.set(item.id, item);
        for (const item of incoming || []) merged.set(item.id, item);
        return Array.from(merged.values());
    };

    // Adopt the initial page once. main.jsx renders loading -> data, and the
    // retry path may re-render with page 0 again, so guard with a ref.
    useEffect(() => {
        if (loading || error || initialized.current) return;
        initialized.current = true;
        setList({ events, businesses, locations });
        setHasMoreState(hasMore);
    }, [loading, error, events, businesses, locations, hasMore]);

    const fetchNext = async () => {
        if (!loadMorePage || loadingMore || !list) return;
        setLoadingMore(true);
        setLoadError(false);
        const nextOffset = list.events.length;
        const data = await loadMorePage({ offset: nextOffset });
        setLoadingMore(false);
        if (data && data.feed) {
            const feed = data.feed;
            setList((prev) => ({
                events: mergeById(prev.events, feed.events),
                businesses: mergeById(prev.businesses, feed.businesses),
                locations: mergeById(prev.locations, feed.locations),
            }));
            setHasMoreState(Boolean(feed.hasMore) && (feed.events || []).length > 0);
        } else {
            setLoadError(true);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="post animate-pulse">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="w-10 h-10 rounded-full bg-gray-200" />
                            <span className="block h-4 w-40 rounded bg-gray-200" />
                        </div>
                        <span className="block h-5 w-3/4 rounded bg-gray-200 mb-2" />
                        <span className="block h-3 w-full rounded bg-gray-200 mb-1.5" />
                        <span className="block h-3 w-5/6 rounded bg-gray-200" />
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="post text-center py-12">
                <p className="text-gray-600 mb-4">We couldn't load the latest events.</p>
                <a href="/events/" className="post-cta inline-flex">See all events</a>
            </div>
        );
    }

    const feedEvents = (list && list.events) || [];

    // One deal per feed-ad slot, persisted across re-renders so the shared
    // pool cursor advances exactly once per slot (not once per render).
    const adForSlot = (slot) => {
        if (!allocatedAds.current[slot]) {
            allocatedAds.current[slot] = nextAd();
        }
        return allocatedAds.current[slot];
    };

    const renderEvent = (event) => {
        const business = (list.businesses || []).find(b => b.id === event.businessId);
        const location = resolveEventLocation(event, business, list.locations || []);
        return (
            <EventListItem
                key={event.id}
                event={event}
                business={business}
                location={location}
                analytics={analytics}
            />
        );
    };

    // Interleave sponsors: the 4th post (index 3), then every 7 posts after
    // (indices 10, 17, ...) with exactly 6 non-ads between them. Ads are
    // placed by FINAL index (each ad shifts the events after it), so the slot
    // for slot k sits at final index 3 + 7k.
    const items = [];
    let adsInserted = 0;
    for (let i = 0; i < feedEvents.length; i++) {
        const finalIndex = i + adsInserted;
        if (finalIndex >= 3 && finalIndex % 7 === 3) {
            const slot = (finalIndex - 3) / 7;
            const ad = adForSlot(slot);
            if (ad) {
                items.push(<AdCard key={`ad-${slot}`} ad={ad} analytics={analytics} />);
                adsInserted += 1;
            }
        }
        items.push(renderEvent(feedEvents[i]));
    }

    return (
        <div>
            <div className="space-y-6">
                {items.length === 0 ? (
                    <div className="post text-center py-12">
                        <p className="text-gray-600">No events scheduled right now — check back soon.</p>
                    </div>
                ) : (
                    items
                )}
            </div>

            {/* Load more button (manual pagination so the footer stays reachable) */}
            {items.length > 0 && !loadError && (hasMoreState || loadingMore) && (
                <div className="text-center py-8">
                    <button
                        type="button"
                        onClick={() => fetchNext()}
                        disabled={loadingMore}
                        className="post-cta post-cta--ghost no-underline"
                    >
                        {loadingMore ? (
                            <>
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                                Loading...
                            </>
                        ) : (
                            'Load more'
                        )}
                    </button>
                </div>
            )}

            {/* Failed page load → manual retry */}
            {loadError && (
                <div className="text-center py-6">
                    <p className="text-sm text-gray-500 mb-3">Couldn't load more events.</p>
                    <button type="button" onClick={() => fetchNext()} className="tab-pill tab-pill--inactive no-underline">
                        Try again
                    </button>
                </div>
            )}

            {/* End of the feed */}
            {items.length > 0 && !hasMoreState && !loadingMore && (
                <div className="text-center py-8">
                    <p className="text-gray-500 text-sm mb-3">You're all caught up!</p>
                    <a href="/events/" className="text-brand-700 hover:text-brand-800 font-semibold text-sm no-underline">
                        Browse the full events directory
                    </a>
                </div>
            )}
        </div>
    );
}